import { InfoCircleOutlined } from '@ant-design/icons'
import {
  Button,
  CardsCol,
  CardsRow,
  ConsoleCard,
  message,
  modalConfirm,
  Popover,
} from 'admin-web-ui'
import { isAuthorized, ProjectGroup } from 'admin-web-utils'
import React, { useCallback, useEffect } from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { useProjectContext } from '../../../containers/Project'
import { downloadTextFile } from '../../../utils/download'
import { issueTestRegistrationKey } from './issue-test-registration-key'
import {
  revokeTestRegistrationKey,
  revokeTestRegistrationKeyByOwner,
} from './revoke-test-registration-key'
import { TestRegistrationKeyList } from './TestRegistrationKeyList'
import { fetchKeysAndUsers, UserKeyInfo } from './user-key-info'

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: ${({ theme }) => theme.colors.sudoBlue};
`

export const TestRegistrationKeyIconPopover: React.FC = () => {
  const content = (
    <p>
      A test registration key allows you to
      <br /> customize your registration status as
      <br /> seen by the application for testing purposes.
    </p>
  )

  return (
    <Popover title="Test Keys" content={content} trigger="hover">
      <StyledInfoCircleOutlined />
    </Popover>
  )
}

export const TestRegistrationKeys: React.FC = () => {
  const { me, projectClient, currentProject } = useProjectContext()
  const isAdmin = isAuthorized(
    ProjectGroup.Admin,
    me.authGroups,
    currentProject?.id,
  )

  const [{ loading: userKeyInfoLoading, value: userKeyInfo }, getUserKeyInfo] =
    useAsyncFn(
      async (): Promise<UserKeyInfo[]> =>
        fetchKeysAndUsers(isAdmin, projectClient),
    )

  useEffect(() => {
    getUserKeyInfo()
  }, [getUserKeyInfo])

  const handleModalConfirm = useCallback(async () => {
    const testRegKeyResult = await issueTestRegistrationKey(projectClient)
    if (testRegKeyResult) {
      downloadTextFile(`register_key.private`, testRegKeyResult.privateKeyData)
      getUserKeyInfo()
    }
  }, [projectClient, getUserKeyInfo])

  const showConfirmModal = useCallback(() => {
    modalConfirm({
      title: 'Generate Test Registration Key',
      content: `
        Test registration keys can only be downloaded once. Confirming will
        automatically prompt you to download your newly generated test key.
        Do you wish to proceed with this key creation?
      `,
      onOk: handleModalConfirm,
    })
  }, [handleModalConfirm])

  const revokeKeyHandler = useCallback(
    async (keyId: string) => {
      try {
        if (isAdmin) {
          await revokeTestRegistrationKeyByOwner(keyId, projectClient)
        } else {
          await revokeTestRegistrationKey(keyId, projectClient)
        }
        getUserKeyInfo()
        message.success(`Key deleted: ${keyId}`)
      } catch {
        message.error('Key failed to delete, please try again.')
      }
    },
    [projectClient, getUserKeyInfo, isAdmin],
  )

  const userHasKey = !!userKeyInfo?.some(
    (keyInfo) => keyInfo.sub === me.username,
  )

  return (
    <CardsRow>
      <CardsCol span={24}>
        <ConsoleCard
          title={
            <span>
              Test Registration Keys <TestRegistrationKeyIconPopover />
            </span>
          }
          extra={
            <Button
              className="automation-TestKeyButton"
              id="RegistrationTestKey__gen-key-btn"
              type="primary"
              shape="round"
              onClick={showConfirmModal}
              disabled={userHasKey || userKeyInfoLoading}
            >
              Generate Test Registration Key
            </Button>
          }
        >
          <TestRegistrationKeyList
            dataSource={userKeyInfo ?? []}
            loading={userKeyInfoLoading}
            onRevoke={revokeKeyHandler}
            isAdmin={isAdmin}
          />
        </ConsoleCard>
      </CardsCol>
    </CardsRow>
  )
}
